import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatToolbarModule } from '@angular/material/toolbar';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatCalendar } from '@angular/material/datepicker';
import { IcalendarService } from '../services/cal/icalendar.service';
import { NewsItem } from '../services/news-items/news-items';
import { NewsItemsService } from '../services/news-items/news-items.service';
import { NewsItemCardComponent } from '../pages/hub-extensions/news-item-card/news-item-card.component';
import { SafetyCounterService } from '../services/safety-counter/safety-counter.service';

@Component({
    selector: 'app-hub',
    imports: [
        CommonModule,
        MatButtonModule,
        MatCheckboxModule,
        MatInputModule,
        MatRadioModule,
        MatFormFieldModule,
        MatCardModule,
        FormsModule,
        ReactiveFormsModule,
        MatDatepickerModule,
        MatToolbarModule,
        RouterModule,
        MatIconModule,
        MatDividerModule,
        MatCalendar,
        NewsItemCardComponent,
    ],
    templateUrl: './hub.component.html',
    styleUrl: './hub.component.css'
})
export class HubComponent implements OnInit, OnDestroy {
  currentTab: number = 0;
  events = [];
  importantDates: any[] = [{
    description: 'No Important Dates Upcoming',
    info: ''
    }];
  upcomingAnniversaries: any[] = [];
  upcomingBdays: any[] = [];
  selectedDate: Date | null = null;
  currentNewsIndex = 0;
  newsInterval: any;
  newsIsHovered = false;

  latestNewsletter: NewsItem | undefined = undefined;
  carouselAnnouncements: NewsItem[] = [];
  daysSinceInjury: number | null = null;

  constructor(
    private icalendarService: IcalendarService,
    private newsItemsService: NewsItemsService,
    private safetyCounterService: SafetyCounterService,
  ) {}

  dateClass = (d: Date): string => {
    const today = new Date();
    if (d.toDateString() === today.toDateString()) {
      return 'today-date';
    } else if (d.getMonth() === 4 && d.getDate() === 25) {
      return 'special-date';
    }
    return '';
  };

  loadUpcomingImportantDates(): void {
    this.icalendarService.fetchIcalImportantDates().subscribe(
      (data) => {
        if (data && Object.keys(data).length > 0) {
          this.importantDates = Object.keys(data).map((key) => {
            const event = data[key];
            const eventDate = new Date(event.start);
            const eventSummary = event.summary;

            const eventNameMatch = eventSummary.match(/(?<=- ).*/);
            const eventInfoMatch = eventSummary.match(/^(.*?)(?: -)/);

            const eventName = eventNameMatch ? eventNameMatch[0] : '';
            const eventInfo = eventInfoMatch ? eventInfoMatch[1] : '';

            this.importantDates.sort(
              (a, b) => new Date(a.sortDate).getTime() - new Date(b.sortDate).getTime()
            );

            return {
              sortDate: eventDate,
              date: `${eventDate.getUTCMonth() + 1}/${eventDate.getUTCDate()}`,
              description: eventName,
              info: eventInfo
            };
          });
        }
      },
      (error) => {
        console.error('Error fetching important dates:', error);
      }
    );
  }

  loadUpcomingAnniversaries(): void {
    this.icalendarService.fetchIcalAnniversaryData().subscribe(
      (data) => {
        this.upcomingAnniversaries = Object.keys(data).map((key) => {
          const event = data[key];
          const eventDate = new Date(event.start);
          const eventSummary = event.summary;

          const eventNameMatch = eventSummary.match(/^(.*?)\s*\(\s*/);
          const yearMatch = eventSummary.match(/(?<=\s\()\s*(.*?)\s*(?=\))/);

          const name = eventNameMatch ? eventNameMatch[1] : '';
          let year = yearMatch ? yearMatch[1] : '';

          year = year.replace(' yrs', 'yr Anniversary');
          year = year.replace(' yr', 'yr Anniversary');

          return {
            sortDate: eventDate,
            date: `${eventDate.getUTCMonth() + 1}/${eventDate.getUTCDate()}`,
            name: name,
            year: year,
          };
      });

        this.upcomingAnniversaries.sort(
          (a, b) => new Date(a.sortDate).getTime() - new Date(b.sortDate).getTime()
        );
      },
      (error) => {
        console.error('Error fetching upcoming anniversaries:', error);
      }
    );
  }

  loadUpcomingBday(): void {
    this.icalendarService.fetchIcalBdayData().subscribe(
      (data) => {
        this.upcomingBdays = Object.keys(data).map((key) => {
          const event = data[key];
          const eventDate = new Date(event.start);
          const summary = event.summary;
          const nameMatch = summary.match(/^(.+?)\s-\sBirthday$/);
          const name = nameMatch ? nameMatch[1] : '';

          return {
            sortDate: eventDate,
            date: `${eventDate.getUTCMonth() + 1}/${eventDate.getUTCDate()}`,
            name: name,
          };
        });

        this.upcomingBdays.sort(
          (a, b) => new Date(a.sortDate).getTime() - new Date(b.sortDate).getTime()
       );
      },
      (error) => {
        console.error('Error fetching upcoming birthdays:', error);
      }
    );
  }

  nextNews(): void {
    this.currentNewsIndex = (this.currentNewsIndex + 1) % this.carouselAnnouncements.length;
    this.smoothTransition();
  }

  loadNewsItems(): void {
    this.newsItemsService.getNewsItems({ displayIn: 'current-newsletter' }).subscribe(items => {
      this.latestNewsletter = items[0];
    });
    this.newsItemsService.getNewsItems({ displayIn: 'carousel' }).subscribe(items => {
      this.carouselAnnouncements = items
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      if (this.carouselAnnouncements.length > 0) {
        this.startNewsCarousel();
      }
    });
  }

  loadSafetyCounter(): void {
    this.safetyCounterService.getCounter().subscribe({
      next: (data) => (this.daysSinceInjury = data.daysSince),
      error: (err) => {
        console.error('Failed to load safety counter:', err);
        this.daysSinceInjury = null;
      },
    });
  }

  ngOnInit(): void {
    this.loadUpcomingImportantDates();
    this.loadUpcomingAnniversaries();
    this.loadUpcomingBday();
    this.loadNewsItems();
    this.loadSafetyCounter();
  }

  ngOnDestroy(): void {
    if (this.newsInterval) {
      clearInterval(this.newsInterval);
    }
  }

  prevNews(): void {
    this.currentNewsIndex =
      (this.currentNewsIndex - 1 + this.carouselAnnouncements.length) %
      this.carouselAnnouncements.length;
    this.smoothTransition();
  }

  smoothTransition(): void {
    const newsCards = document.querySelectorAll('.news-card');
    newsCards.forEach((card) => {
      card.classList.remove('active');
    });

    setTimeout(() => {
      newsCards[this.currentNewsIndex].classList.add('active');
    }, 50);
  }

  startNewsCarousel(): void {
    this.newsInterval = setInterval(() => {
      if (!this.newsIsHovered) {
        this.nextNews();
      }
    }, 7000);
  }

  switchTab(tabIndex: number): void {
    this.currentTab = tabIndex;
  }
}
